/**
 * Append-only event ledger envelope (§14.2).
 *
 * The ledger is *not* an event-sourced copy of every entity — relational tables
 * own current execution state (ADR-001). The ledger exists to answer AC-030:
 * who, when, why, with which model, prompt, skills, tools and workspace, and at
 * what cost, each state change happened. Ordering is guaranteed only within an
 * aggregate; there is deliberately no global total order to reason about.
 */
export const EVENT_SCHEMA_VERSION = 1;
/**
 * Canonical event type names.
 *
 * Kept as a closed list so that projections, the Office and the audit API cannot
 * drift apart from the emitters. Adding a case here forces every exhaustive
 * `switch` in the projections to be updated.
 */
export const EVENT_TYPES = [
    // ingest
    'inbox.delivery.received',
    'inbox.delivery.duplicate_suppressed',
    'inbox.delivery.rejected',
    'task.snapshot.observed',
    'task.snapshot.unchanged',
    'task.snapshot.self_caused',
    'task.unmanaged.ignored',
    // runs and stages
    'run.created',
    'run.completed',
    'run.failed',
    'run.cancelled',
    'run.superseded',
    'stage.created',
    'stage.claim_requested',
    'stage.claim_projected',
    'stage.claim_projection_skipped',
    'stage.claim_projection_failed',
    'stage.dispatch_prepared',
    'stage.transition_requested',
    'stage.transition_committed',
    'stage.transition_unknown',
    'stage.transition_conflicted',
    'stage.blocked_human',
    'stage.human_resolved',
    'stage.failed',
    'stage.cancelled',
    'stage.superseded',
    // attempts
    'attempt.prepared',
    'attempt.delivering',
    'attempt.accepted',
    'attempt.started',
    'attempt.heartbeat',
    /*
     * A slice of what the model is producing, while it is producing it
     * (ADR-114).
     *
     * Coalesced by the worker rather than emitted per token: the ledger is an
     * audit trail, and a token log would bury every other event in it. Carries
     * `internal` redaction because model output is not fit for a board comment.
     */
    'attempt.output',
    'attempt.checkpoint',
    'attempt.completed',
    'attempt.failed',
    'attempt.cancelled',
    'attempt.unknown',
    'attempt.unresolved',
    'attempt.superseded',
    'attempt.late_result_rejected',
    // Stop now (spec §3): the operator's Ctrl-C, and how it ended.
    'attempt.stop_requested',
    'attempt.stopped',
    'attempt.stop_timeout',
    'role.stop_requested',
    'role.stopped',
    'role.resumed',
    'team.stop_requested',
    'team.stopped',
    'team.resumed',
    // leases
    'lease.granted',
    'lease.renewed',
    'lease.released',
    'lease.expired',
    'lease.revoked',
    'lease.superseded',
    // evidence, gates, approvals
    'evidence.recorded',
    'evidence.rejected_stale',
    'gate.evaluated',
    'approval.recorded',
    'approval.invalidated',
    'human.question.raised',
    'human.decision.recorded',
    'checklist.item_reported',
    // rework
    'rework.returned',
    'rework.budget_exhausted',
    'rework.escalated_human',
    // budgets
    'budget.reserved',
    'budget.settled',
    'budget.released',
    'budget.soft_limit_reached',
    'budget.hard_limit_reached',
    // outbox and external mutations
    'outbox.enqueued',
    'outbox.confirmed',
    'outbox.failed',
    'outbox.unknown',
    'outbox.marker_found',
    'outbox.marker_absent',
    'outbox.abandoned',
    // providers, routing, threads
    'routing.decided',
    'provider.session.created',
    'provider.session.resumed',
    'provider.session.lost',
    'provider.rate_limited',
    'thread.entry.appended',
    'thread.compacted',
    'thread.context_pack.built',
    'memory.promoted',
    // workspaces and tools
    'workspace.allocated',
    'workspace.quarantined',
    'workspace.released',
    'tool.invoked',
    'tool.denied',
    'tool.completed',
    'resource.reaped',
    'resource.leak_detected',
    // provider-neutral agent card actions
    'card.label.set',
    'card.label.removed',
    'card.change.link.requested',
    'card.move.requested',
    'card.move.verified',
    'card.move.rejected',
    'card.move.conflicted',
    'card.move.unknown',
    // provider-neutral deployment and release history
    'deployment.created',
    'deployment.attempt.started',
    'deployment.attempt.reported',
    // provider-neutral webhook management and durable dispatch
    'webhook.endpoint.created',
    'webhook.endpoint.updated',
    'webhook.endpoint.paused',
    'webhook.endpoint.resumed',
    'webhook.endpoint.secret_rotated',
    'webhook.batch.retry_requested',
    'webhook.batch.delivered',
    'webhook.batch.failed',
    // config
    'team.version.drafted',
    'team.version.validated',
    'team.version.activated',
    'team.paused',
    'team.resumed',
    'role.paused',
    'role.resumed',
    // nodes, incidents, integrity
    'node.enrolled',
    'node.heartbeat',
    'node.unreachable',
    'incident.opened',
    'incident.closed',
    'integrity.violation_detected',
];
const EVENT_TYPE_SET = new Set(EVENT_TYPES);
export function isKnownEventType(value) {
    return EVENT_TYPE_SET.has(value);
}
export function encodeCursor(cursor) {
    return `${cursor.ledgerSequence}:${cursor.eventId}`;
}
export function decodeCursor(value) {
    const separator = value.indexOf(':');
    if (separator <= 0)
        return null;
    const ledgerSequence = Number(value.slice(0, separator));
    const eventId = value.slice(separator + 1);
    if (!Number.isSafeInteger(ledgerSequence) || ledgerSequence < 0 || eventId.length === 0)
        return null;
    return { ledgerSequence, eventId: eventId };
}
//# sourceMappingURL=events.js.map